import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Home } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { createOrder, OrderItem, CustomerDetails } from '../services/orderService';
import { redeemCoupon } from '../services/couponService';

const PageContainer = styled.div`
  padding-top: var(--header-h);
  min-height: 100vh;
  background: #f5f5f5;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #28a745;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
  
  svg {
    color: white;
    width: 50px;
    height: 50px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #333;
  margin-bottom: 15px;
  font-weight: 600;
`;

const Message = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 30px;
  line-height: 1.6;
`;

const PaymentInfo = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  text-align: left;
  
  .info-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    font-size: 0.95rem;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .label {
      color: #666;
      font-weight: 500;
    }
    
    .value {
      color: #333;
      font-weight: 600;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  
  &.primary {
    background: #000;
    color: white;
    
    &:hover {
      background: #333;
    }
  }
  
  &.secondary {
    background: #f0f0f0;
    color: #333;
    
    &:hover {
      background: #e0e0e0;
    }
  }
`;

const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart, cartItems } = useCart();
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(true);
  
  const paymentId = searchParams.get('paymentId');
  const conversationId = searchParams.get('conversationId');

  useEffect(() => {
    // Sipariş oluştur
    const createOrderFromPayment = async () => {
      try {
        // LocalStorage'dan müşteri bilgilerini al
        const storedCustomerData = localStorage.getItem('checkout_customer_data');
        const storedShippingAddress = localStorage.getItem('checkout_shipping_address');
        const storedBillingAddress = localStorage.getItem('checkout_billing_address');
        const storedTotals = localStorage.getItem('checkout_totals');
        
        if (!storedCustomerData || !paymentId) {
          console.error('Müşteri bilgileri veya ödeme ID bulunamadı');
          setIsCreatingOrder(false);
          return;
        }

        const customerData: CustomerDetails = JSON.parse(storedCustomerData);
        const shippingAddress = storedShippingAddress ? JSON.parse(storedShippingAddress) : undefined;
        const billingAddress = storedBillingAddress ? JSON.parse(storedBillingAddress) : undefined;

        // Sepet ürünlerini sipariş formatına çevir
        const orderItems: OrderItem[] = cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          size: item.size,
          image: item.image,
        }));

        // Sipariş oluştur
        // Tutar kalemleri odeme sayfasindan gelir; kargo ve kupon indirimi
        // dahil edilmis nihai tutar siparise boylece dogru islenir.
        const totals = storedTotals ? JSON.parse(storedTotals) : undefined;

        const result = await createOrder(
          orderItems,
          customerData,
          paymentId,
          conversationId || undefined,
          shippingAddress,
          billingAddress,
          totals
        );

        if (result.success && result.orderNumber) {
          setOrderNumber(result.orderNumber);
          // LocalStorage'ı temizle
          // Kupon kullanildi olarak isaretlenir. Odeme tamamlandiktan SONRA
          // yapilir; odeme yarida kalirsa kupon hakki yanmaz.
          if (totals?.couponCode) {
            await redeemCoupon(totals.couponCode);
          }

          localStorage.removeItem('checkout_totals');
          localStorage.removeItem('checkout_customer_data');
          localStorage.removeItem('checkout_shipping_address');
          localStorage.removeItem('checkout_billing_address');
          // Sepeti temizle
          clearCart();
        } else {
          console.error('Sipariş oluşturulamadı:', result.error);
        }
      } catch (error) {
        console.error('Sipariş oluşturma hatası:', error);
      } finally {
        setIsCreatingOrder(false);
      }
    };

    if (paymentId && cartItems.length > 0) {
      createOrderFromPayment();
    } else {
      setIsCreatingOrder(false);
      clearCart();
    }
  }, [paymentId, conversationId, cartItems, clearCart]);

  return (
    <PageContainer>
      <Container>
        <SuccessIcon>
          <CheckCircle />
        </SuccessIcon>
        
        <Title>Ödeme Başarılı!</Title>
        {isCreatingOrder ? (
          <Message>
            Siparişiniz oluşturuluyor, lütfen bekleyin...
          </Message>
        ) : (
          <>
            <Message>
              Siparişiniz başarıyla alındı. Ödeme işleminiz tamamlandı.
              Sipariş detayları e-posta adresinize gönderilecektir.
            </Message>

            {(orderNumber || paymentId || conversationId) && (
              <PaymentInfo>
                {orderNumber && (
                  <div className="info-row">
                    <span className="label">Sipariş Numaranız:</span>
                    <span className="value" style={{ color: '#000', fontSize: '1.2rem', fontWeight: 'bold' }}>
                      {orderNumber}
                    </span>
                  </div>
                )}
                {paymentId && (
                  <div className="info-row">
                    <span className="label">Ödeme ID:</span>
                    <span className="value">{paymentId}</span>
                  </div>
                )}
                {orderNumber && (
                  <div className="info-row" style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #ddd' }}>
                    <span className="label" style={{ fontSize: '0.9rem', color: '#666' }}>
                      Siparişinizi takip etmek için sipariş numaranızı kullanabilirsiniz.
                    </span>
                  </div>
                )}
              </PaymentInfo>
            )}
          </>
        )}

        <ButtonGroup>
          <Button className="primary" onClick={() => navigate('/')}>
            <Home size={18} />
            Ana Sayfaya Dön
          </Button>
          <Button className="secondary" onClick={() => navigate('/ürünlerimiz')}>
            <ArrowRight size={18} />
            Alışverişe Devam Et
          </Button>
        </ButtonGroup>
      </Container>
    </PageContainer>
  );
};

export default PaymentSuccessPage;
